/**
 * English dictionary. Its shape is the Dictionary type: ar.ts must provide every key.
 * The product name appears only here and in ar.ts (it is temporary and replaceable).
 * Messages with parameters are functions, or templates with {placeholders} for API issue params.
 */
export const en = {
  app: {
    name: 'cTax',
    pageTitle: (page: string) => `${page} · cTax`,
    skipToContent: 'Skip to main content',
    homeLabel: 'cTax home',
  },
  nav: {
    label: 'Work hubs',
    today: 'Today',
    billing: 'Billing',
    incoming: 'Incoming',
    filing: 'Filing',
    records: 'Records',
    organization: 'Organization',
    hubPages: (hub: string) => `${hub}: pages`,
    notBuiltTag: 'Not built yet',
  },
  modules: {
    billingDocuments: {
      name: 'Billing documents',
      line1:
        'Draft, issue and send invoices to the Tax Authority, then follow each one to its final state.',
      line2: 'Copies and PDF prints are included.',
    },
    receipts: {
      name: 'E-receipts',
      line1: 'Issue electronic receipts from a registered POS device.',
      line2: 'Each POS device keeps its own numbering and connection details.',
    },
    paymentTracking: {
      name: 'Payment tracking',
      line1: 'Record what buyers paid and match it to their documents.',
      line2: 'An internal record only: no money moves.',
    },
    receivedDocuments: {
      name: 'Received documents',
      line1: 'See the documents your suppliers issued to you on the Tax Authority system.',
      line2: 'Review each one and accept or reject it within the allowed window.',
    },
    filing: {
      name: 'Period overview',
      line1:
        'VAT for the month and withholding for the quarter, grouped by obligation, with a link to each statement.',
      line2:
        'Figures come only from documents issued here. They are for review, not a filed return.',
    },
    filingSales: {
      name: 'Period sales',
      line1:
        'Every sales document on the days you choose, with its values, taxes and totals across all pages.',
      line2: 'Count documents by their document date or by the date ETA received them.',
    },
    filingPurchases: {
      name: 'Period purchases',
      line1:
        'Documents your suppliers issued to your organization on the days you choose, with their values and taxes.',
      line2: 'Built with the received documents page, which it reads from.',
    },
    filingVat: {
      name: 'VAT workpaper',
      line1:
        'The output side, the input side and the period result. Every figure opens the documents behind it.',
      line2:
        'How figures are classified is under tax review and stays hidden until it is approved.',
    },
    filingWithholding: {
      name: 'Withholding statement',
      line1: 'Withholding (T4) lines on documents, per counterparty and per quarter.',
      line2: 'Built with the received documents page. Its treatment is under tax review.',
    },
    filingFiles: {
      name: 'Prepared files',
      line1:
        'Prepare a file from any statement in the background, then save it here once it is ready.',
      line2: 'Each file stays available for a set time, shown next to it.',
    },
    buyers: {
      name: 'Buyer register',
      line1: 'All your buyers in one place, with their details, addresses and balances.',
      line2: 'Until then, add a buyer from inside an invoice.',
    },
    suppliers: {
      name: 'Supplier register',
      line1: 'Supplier details and what you owe each of them.',
      line2: 'Built together with received documents.',
    },
    items: {
      name: 'Item catalogue',
      line1: 'Your goods and services with their Tax Authority codes and default taxes.',
      line2: 'Until then, add an item from inside an invoice.',
    },
    orgSetup: {
      name: 'Organization setup',
      line1:
        'Company details, branches, taxes, document numbering and the Tax Authority connection.',
      line2: 'The essential setup steps are already available on the Today page.',
    },
    team: {
      name: 'Team and access',
      line1: 'Invite colleagues and decide what each of them may do in each organization.',
      line2: 'Access rules are already enforced on the server.',
    },
    imports: {
      name: 'Upload data from a file',
      line1: 'Bring buyers, suppliers, items and invoices in from prepared files.',
      line2: 'Every error is listed before anything is saved.',
    },
    notes: {
      name: 'Credit and debit notes',
      line1: 'A note is issued only against invoices that are valid at the Tax Authority.',
      line2: 'A note never goes beyond what the referenced invoice allows.',
    },
  },
  notBuilt: {
    chip: 'Not built yet',
    plannedBlock: (block: string) => `Planned block: ${block} of 10`,
    whatItWillDo: 'What this page will do',
    goInvoices: 'Open billing documents',
    unknown: 'This page does not exist.',
  },
  testEnv: {
    strip: 'Test environment — nothing reaches the Tax Authority',
    detail:
      'Documents go to a simulator; names and numbers are made up. Do not enter real taxpayer data.',
    more: 'About the test environment',
  },
  common: {
    optional: 'Optional',
    loading: 'Loading…',
    searching: 'Searching…',
    searchFailed: 'Search failed. Try again.',
    noMatches: 'No matches',
    clearSelection: 'Clear selection',
    openCalendar: 'Open calendar',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    retry: 'Try again',
    cancel: 'Close without saving',
    notNow: 'Not now',
    dismiss: 'Dismiss',
    copied: 'Copied',
    notSet: 'Not set',
  },
  pagination: {
    label: 'Pagination',
    range: (from: string, to: string, total: string) => `${from}–${to} of ${total}`,
    page: (page: string, pages: string) => `Page ${page} of ${pages}`,
    pageSize: 'Rows per page',
    previous: 'Previous page',
    next: 'Next page',
    first: 'First page',
    last: 'Last page',
  },
  shell: {
    organizations: 'Organizations',
    chooseOrganization: 'Choose organization',
    orgSwitcherLabel: (name: string | null) =>
      name ? `Organization: ${name}. Switch organization` : 'Choose organization',
    allOrganizations: 'All organizations',
    createOrganization: 'New organization',
    current: 'Current',
    switchLanguage: 'Switch language',
    otherLanguageName: 'العربية',
    accountMenu: 'Your account',
    theme: 'Theme',
    themes: { system: 'Match system', light: 'Light', dark: 'Dark' },
    signOut: 'Sign out',
  },
  palette: {
    open: 'Search or jump to…',
    title: 'Command palette',
    searchLabel: 'Search commands',
    placeholder: 'Type a page, organization or command…',
    empty: 'No matching commands',
    sectionGo: 'Go to',
    sectionOrganizations: 'Organizations',
    sectionPreferences: 'Language and look',
    switchTo: (name: string) => `Switch to ${name}`,
    switchToArabic: 'Switch to Arabic',
    switchToEnglish: 'Switch to English',
    themeLight: 'Use light theme',
    themeDark: 'Use dark theme',
    themeSystem: 'Use system theme',
    hintNavigate: 'move',
    hintRun: 'open',
    hintClose: 'exit',
  },
  env: {
    mock: 'Sandbox',
    preprod: 'ETA pre-production',
    production: 'ETA production',
  },
  roles: {
    owner: 'Owner',
    admin: 'Administrator',
    accountant: 'Accounts officer',
    sales: 'Sales staff',
    viewer: 'Viewer',
  },
  auth: {
    brandHeadline: 'E-invoicing, tax and accounting for businesses in Egypt.',
    brandPoints: [
      'Invoices calculated exactly as the Egyptian Tax Authority validates them.',
      'Arabic and English, right-to-left first.',
      'Every change recorded in an audit trail.',
    ],
    signInTitle: 'Sign in',
    signInSubtitle: 'Welcome back. Sign in to continue to your organizations.',
    email: 'E-mail address',
    password: 'Your password',
    signIn: 'Sign in',
    invalidCredentials: 'The email or password is incorrect.',
    sessionExpired: 'Your session ended. Sign in again to continue.',
    noAccount: 'New here?',
    createAccount: 'Create an account',
    registerTitle: 'Create your account',
    registerSubtitle: 'You will set up your organization in the next step.',
    displayName: 'Your name',
    passwordHint: 'At least 10 characters, and not based on your email address.',
    register: 'Create account',
    haveAccount: 'Already have an account?',
  },
  orgs: {
    eyebrow: 'Organization',
    chooseTitle: 'Your organizations',
    chooseSubtitle: 'Choose the organization to work in.',
    emptyTitle: 'No organizations yet',
    emptyBody: 'Create your organization to start issuing e-invoices.',
    createTitle: 'New organization',
    createSubtitle: 'The legal entity that issues invoices to the Egyptian Tax Authority.',
    identitySection: 'Identity',
    name: 'Organization name',
    legalName: 'Legal name',
    legalNameHint: 'As registered with the Tax Authority, if different from the name above.',
    rin: 'Tax registration number (RIN)',
    rinHint: '9 digits. Needed to issue invoices; required for the production environment.',
    baseCurrency: 'Base currency',
    environment: 'ETA environment',
    environmentIntro:
      'Where this organization’s documents will be sent once submission is enabled.',
    envDescriptions: {
      mock: 'Simulated connection for trying the system. Nothing reaches the Tax Authority.',
      preprod: 'The Tax Authority’s pre-production system, for integration testing.',
      production: 'The live Tax Authority system. Documents have legal effect.',
    },
    environmentLockedTitle: 'Chosen once',
    environmentLocked:
      'The environment cannot be changed after the organization is created. Use a separate organization for testing.',
    create: 'Create organization',
    created: 'Organization created.',
    profileTitle: 'Organization profile',
  },
  address: {
    country: 'Country or territory',
    governate: 'Governorate or state',
    regionCity: 'City / region',
    street: 'Street name',
    buildingNumber: 'Building or house number',
    more: 'More address details',
    postalCode: 'Postal or ZIP code',
    floor: 'Floor number',
    room: 'Room or office number',
    landmark: 'Nearby landmark',
    additionalInformation: 'Notes on the address',
  },
  validation: {
    decimalInvalid: 'Enter a number, for example 1250.50',
    decimalTooManyDecimals: 'Use at most 5 decimal places.',
    decimalTooLarge: 'This number is too large.',
  },
  errors: {
    unknown: 'Something went wrong. Please try again.',
    reference: 'Reference',
    copyReference: 'Copy reference',
    summaryTitle: (count: number) =>
      count === 1 ? 'Fix 1 problem to continue' : `Fix ${count} problems to continue`,
    issueFallback: 'This value is not accepted ({issue}).',
    retryInSeconds: 'Try again in {seconds} seconds.',
    retryInMinutes: 'Try again in {minutes} minutes.',
    notFoundTitle: 'Page not found',
    notFoundBody: 'The address may be mistyped, or the page has moved.',
    orgNotFoundTitle: 'Organization not available',
    orgNotFoundBody: 'It does not exist, or you are not a member of it.',
    goHome: 'Go to the start page',
    codes: {
      validation_error: 'Some details need your attention.',
      bad_request: 'The request could not be processed.',
      unauthenticated: 'Your session has ended. Please sign in again.',
      permission_denied: 'You do not have permission to do this.',
      not_found: 'This record does not exist, or you do not have access to it.',
      document_state_conflict:
        'This document has already been issued and can no longer be changed.',
      duplicate_document: 'A record with these details already exists.',
      version_conflict: 'Someone else changed this record. Reload it to see the latest version.',
      idempotency_conflict: 'This request was already submitted.',
      rate_limited: 'Too many attempts.',
      eta_auth_failed: 'The Tax Authority rejected the connection credentials.',
      eta_submission_failed: 'The Tax Authority did not accept the submission.',
      eta_unavailable: 'The Tax Authority system is unavailable right now.',
      import_row_error: 'A row in the imported file has a problem.',
      not_implemented: 'This action is not available yet.',
      buyer_email_missing:
        'The buyer has no e-mail for invoices, so nothing was issued. Issue without an e-mail copy, or add the address first.',
      buyer_email_invalid:
        "The buyer's e-mail for invoices is not a valid address, so nothing was issued. Issue without an e-mail copy, or correct the address first.",
      email_not_failed: "The buyer copy's state had changed; the latest state is shown.",
      eta_window_closed: "The Tax Authority's period for this action has ended.",
      active_referencing_documents: 'First cancel the notes based on this invoice.',
      document_frozen: 'The Tax Authority has frozen this document.',
      receiver_actions_mock_only: 'This action is available in the simulator only for now.',
      internal_error: 'Something went wrong on our side. Please try again.',
      network_error: 'Cannot reach the server. Check your connection and try again.',
      unexpected_response: 'The server sent an unexpected response.',
    },
  },
  /** API issue keys (zod codes and domain rules). {placeholders} come from issue params. */
  issues: {
    reason_required: 'Write the reason for the cancellation.',
    reason_too_long: 'The reason is longer than 500 characters.',
    preview_mock_only: 'This preview creates organizations in the test environment only.',
    required: 'Required.',
    too_small: 'Required.',
    too_big: 'Too long.',
    invalid_type: 'Required.',
    invalid_format: 'The format is not valid.',
    invalid_value: 'Choose one of the allowed values.',
    invalid_union: 'This value is not valid.',
    custom: 'This value is not valid.',
    unrecognized_keys: 'Unexpected field.',
    invalid_decimal: 'Enter a valid number.',
    unavailable: 'This email address cannot be used to register.',
    too_short: 'Use at least 10 characters.',
    too_long: 'Use at most 128 characters.',
    contains_email: 'Do not use your email address in the password.',
    unknown: 'Not found in the official ETA code list.',
    kind_mismatch: 'This tax is a fixed amount; it does not take a rate.',
    not_in_list: 'The primary code must be one of the selected codes.',
    business_requires_9_digit_rin: 'A business needs its 9-digit tax registration number.',
    person_national_id_14_digits: 'An Egyptian national ID has 14 digits.',
    foreign_requires_vat_id: 'A foreign buyer needs their tax or VAT ID.',
    foreign_must_not_be_eg: 'A foreign buyer’s address must be outside Egypt.',
    business_in_egypt_must_be_eg: 'A business buyer’s address must be in Egypt.',
    duplicate: 'Already used by another record.',
    goods_only: 'Only goods can be tracked in stock.',
    unknown_party: 'This buyer no longer exists.',
    not_a_customer: 'This party is not set up as a buyer.',
    unknown_item: 'This item no longer exists or was archived.',
    item_without_eta_code: 'This item has no ETA item code.',
    unknown_or_inactive_tax: 'This tax is unknown or inactive.',
    required_for_foreign_currency: 'A rate to EGP is required for a foreign currency.',
    issuer_requires_rin:
      'Add your organization’s tax registration number (RIN) before issuing documents.',
    unknown_branch: 'This branch no longer exists or is inactive.',
    not_registered_for_organization:
      'This taxpayer activity code is not registered for your organization.',
    receiver_requires_tax_id: 'This buyer needs a tax ID before they can be invoiced.',
    receiver_requires_address: 'This buyer needs an address before they can be invoiced.',
    in_future: 'The document date cannot be in the future.',
    before_issue_date: 'The payment due date cannot be before the document date.',
    missing_series: 'No numbering series is set up for this document type.',
    invalid: 'The saved draft cannot be read. Create the document again.',
    must_be_positive: 'Must be greater than zero.',
    must_be_non_negative: 'Cannot be negative.',
    out_of_range: 'Must be between {min} and {max}.',
    exceeds_sales_total: 'The discount cannot be more than the line’s sales total.',
    exceeds_document_total: 'The extra discount cannot be more than the document total.',
    invalid_currency: 'Unknown currency code.',
    must_be_empty_for_egp: 'Not used for documents in Egyptian pounds.',
    unknown_tax_type: 'Unknown tax type.',
    duplicate_tax_type: 'The same tax type is applied twice on this line.',
    unknown_subtype: 'Unknown tax subtype.',
    subtype_parent_mismatch: 'This subtype belongs to tax type {expected}.',
    must_be_zero_for_fixed_amount: 'Fixed-amount taxes take an amount, not a rate.',
    computed_field: 'This value is calculated automatically.',
    required_in_document_currency:
      'Enter the unit price in the document currency (the catalogue price is in EGP).',
    receiver_same_as_issuer: 'The buyer cannot be your own organization.',
    export_requires_foreign_receiver: 'An export invoice needs a foreign buyer.',
    export_receiver_country_is_eg: 'The export buyer must have an address outside Egypt.',
    required_for_export: 'Required on export invoices.',
    unknown_weight_unit: 'Choose a weight unit from the ETA table.',
    currency_not_enabled: 'Enable this currency for your organization first.',
    past_date: 'The document date is in the past. Check that this is intended.',
    cash_customer_unpublished_threshold:
      'Buyer without an ID: the Tax Authority accepts it below a threshold it does not publish.',
    kg_is_keg: 'The unit "KG" is a keg in the ETA table; kilograms are "KGM".',
    tax_not_mapped: 'Tax {taxType}/{subType} is no longer set up, so it was not copied.',
    tax_rate_changed: 'Tax {taxType} rate changed from {from}% to {to}%.',
    immutable: 'This cannot be changed after saving.',
    unknown_code: 'Not found in the official code list.',
    not_allowed_for_type: 'Not allowed for this receipt type.',
    not_64_hex: 'This must be a 64-character identifier.',
    not_iso_4217: 'Not a valid currency code.',
    not_9_digits: 'This must be 9 digits.',
    not_14_digits: 'This must be 14 digits.',
    equals_seller_rin: 'The buyer’s number cannot be your own tax registration number.',
    business_buyer_restricted: 'Business buyers are not allowed for this organization.',
    required_above_threshold: 'Required because the total reaches the reporting threshold.',
    not_5dp: 'Use at most 5 decimal places.',
    must_differ_from_uuid: 'This must differ from the receipt’s own identifier.',
    too_many_lines: 'A receipt carries at most {max} lines.',
    too_few_receipts: 'A submission needs at least {min} receipt.',
    unknown_pos_device: 'This POS device does not exist or is inactive.',
    branch_mismatch: 'This POS device does not belong to the chosen issuing branch.',
    required_with_amount_filter:
      'Choose a currency with an amount limit; amounts in different currencies are not compared.',
    // Note authoring refusals and warnings (Block 11, WP-11.4; sales-notes.md §7).
    unknown_document: 'There is no such document in your organization.',
    not_an_invoice: "This document is not an invoice of this note's family.",
    not_issued: 'The reference invoice is a draft, deleted or replaced.',
    not_valid_at_eta: 'The Tax Authority does not hold the reference invoice valid.',
    cancellation_pending: 'The reference invoice is pending cancellation at the Tax Authority.',
    issued_after_note: 'The reference invoice is newer than the note.',
    same_day_as_note: 'The reference invoice was issued on the same day as the note.',
    cancel_window_open: 'The reference invoice could still be cancelled instead.',
    other_receiver: 'The reference invoice belongs to another buyer.',
    mixed_activity_code: 'The references disagree on the taxpayer activity code.',
    mixed_branch: 'The references disagree on the issuing branch.',
    mixed_currency: 'The references disagree on the document currency.',
    single_for_unidentified_buyer: 'An unidentified buyer allows only one reference.',
    note_family_mismatch: "The reference does not match this note's family.",
    not_in_references: 'This line does not come from any reference invoice.',
    locked_to_reference: 'This field is taken from the reference invoice and cannot be changed.',
    exceeds_reference:
      'This note exceeds what can still be reduced: {remaining} EGP remain of {cap} EGP, after other credit notes of {otherCredits} EGP.',
    differs_from_reference: "The exchange rate differs from the reference invoice's.",
  },
  docStatus: {
    draft: 'Draft',
    issued: 'Issued (final)',
    signing: 'Signing in progress',
    signed: 'Signed with the e-seal',
    superseded: 'Replaced',
    discarded: 'Deleted draft',
  },
  taxStatus: {
    incomplete: 'Incomplete',
    ready: 'Ready to issue',
    signing: 'Signing in progress',
    signed: 'Signed with the e-seal',
    not_submitted: 'Not sent to ETA',
    submitting: 'On its way to ETA',
    submitted: 'Awaiting ETA validation',
    valid: 'Valid',
    invalid: 'Invalid',
    rejected: 'Rejected by receiver',
    not_accepted: 'Not accepted',
    cancelled: 'Cancelled at ETA',
  },
  mock: {
    label: 'MOCK',
    title: 'Test environment: nothing is sent to the Tax Authority',
    scenario: 'Mock answer',
    scenarioHint: 'Testing only: how the mock Tax Authority answers when this draft is sent.',
    scenarios: {
      valid: 'Valid',
      invalid: 'Invalid',
      not_accepted: 'Not accepted at submission',
      rejected: 'Valid, then rejected by receiver',
      cancelled: 'Valid, then cancelled',
      retry_wait: 'Busy first (retry)',
      unknown: 'Answer lost (reconcile)',
    },
  },
  sales: {
    eyebrow: 'Billing',
    title: 'Billing documents',
    newMenu: 'Start a document',
    newInvoice: 'Start an invoice',
    newExportInvoice: 'Start an export invoice',
    startCredit: 'Start a credit note',
    startDebit: 'Start a debit note',
    tabs: {
      invoices: 'Invoices and export invoices',
      credit: 'Credit notes',
      debit: 'Debit notes',
    },
    tabsLabel: 'Document family',
    emptyTitle: 'No invoices yet',
    emptyBody: 'Create your first invoice; it is numbered as soon as you save it.',
    noteEmpty: 'A note starts from a valid invoice of the same buyer.',
    noResultsTitle: 'Nothing matches',
    noResultsBody: 'Try another search, or clear the filters.',
    search: 'Search documents',
    searchPlaceholder: 'Number, buyer or tax ID',
    filter: 'Narrow down',
    filterTitle: 'Narrow down documents',
    filterCount: (n: number) => `Narrow down (${n})`,
    filterStatus: 'Document stage',
    filterTaxStatus: 'ETA status',
    filterFrom: 'From date',
    filterTo: 'To date',
    filterCustomer: 'Buyer',
    filterMin: 'Minimum amount (EGP)',
    filterMax: 'Maximum amount (EGP)',
    applyFilter: 'Show results',
    clearFilter: 'Clear all',
    columns: {
      number: 'Document no.',
      customer: 'Buyer',
      date: 'Document date',
      amount: 'Total in EGP',
      status: 'Document stage',
      taxStatus: 'ETA status',
      actions: 'Document tools',
      updated: 'Last change',
      select: 'Select',
      reference: 'Reference',
    },
    sortBy: (column: string) => `Sort by ${column}`,
    cash: 'Unidentified buyer',
    noCustomer: 'No buyer yet',
    // Block 11 (WP-11.4): the «+n» plain-text accessible name in the reference column.
    referenceMore: (n: number) => `and ${n} more documents`,
    latest: 'Last updated',
    latestEmpty: 'Activity on your documents will appear here.',
    rowActions: (number: string) => `Actions for ${number}`,
    /** Block 11 (T11-03): a rejected document's running objection period, as plain text. */
    declineUntil: (date: string) => `Objection possible until ${date}`,
    declineRunning: 'Objection period running',
    // Block 11 (RB3R11-11): a live cancellation on a valid row, so a request needing a release is
    // visible from the list; honest wording, never "cancelled".
    cancellationRequested: 'Cancellation request being sent',
    cancellationUnconfirmed: 'Cancellation request awaiting confirmation',
    cancellationPending: (date: string) => `Cancellation pending until ${date} unless objected to`,
    cancellationPendingNoDate: 'Cancellation request at the Tax Authority',
    open: 'Open',
    copyAs: 'Copy as',
    copyInvoice: 'Invoice',
    downloadPdf: 'Save a PDF copy',
    issueSend: 'Issue and send',
    delete: 'Delete draft',
    edit: 'Edit draft',
    copied: (number: string) => `Copy created as ${number}.`,
    /** Correct and resend (WP-1b.2): the replacement draft of a refused document. */
    correctionCreated: (number: string) => `Correction draft ${number} created.`,
    correctionOpen: (number: string) => `Correction draft ${number} is already open.`,
    deleted: (number: string) =>
      `${number} deleted. It keeps its number and shows as a deleted draft.`,
    undo: 'Undo',
    pdfReady: 'PDF downloaded.',
    /** E-seal as a separate step (WP-1b.1): sign an issued, unsigned invoice, then send it. */
    sign: 'Sign the invoice',
    sendToEta: 'Send the signed invoice',
    signed: (number: string) => `${number} was signed. The e-seal is stored unchanged.`,
    sent: 'Sent to the Tax Authority with its stored signature.',
    signConfirmTitle: 'Sign this invoice?',
    signConfirmBody:
      'The e-seal signature freezes the invoice. It is stored and sent to the Tax Authority exactly as it was signed, with no further change.',
    signConfirmLabel: 'Sign',
    sendConfirmTitle: 'Send this signed invoice?',
    sendConfirmBody:
      'The signed invoice is sent to the Tax Authority with the exact bytes that were signed.',
    sendConfirmLabel: 'Send',
    updating: 'Updating…',
    kpis: {
      mockNote: 'These figures come from the Tax Authority simulator, not from the real system.',
      drafts: 'Drafts',
      sending: 'With ETA now',
      valid: 'Valid',
      attention: 'Need attention',
      validTotal: 'Valid total (EGP)',
    },
    pageFirst: 'First page',
    pageLast: 'Last page',
    foreignAmount: (amount: string, currency: string) => `${amount} ${currency}`,
    voidNote: 'Deleted draft: the number stays and is never reused.',
    findLabel: 'Find and narrow down',
    views: 'Preset views',
    viewAll: 'All documents',
    viewDeleted: 'Deleted drafts',
    selectRow: (number: string) => `Select ${number}`,
    selectedDoc: (number: string) => `Selected document: ${number}`,
    selectPrompt: 'Select a document to see its tools here.',
    selectAnnounce: (number: string) => `${number} selected. Its tools are above the table.`,
    latestLine: (number: string) => `Last updated: ${number}`,
    /** The list's entry to the batch page (WP-1b.3): a link inside the find panel's own block. */
    batchEntry: {
      title: 'Working on several documents',
      hint: 'Gather up to 100 documents, review them, then run one task on them. Each document gets its own result.',
      link: 'Prepare a batch from these results',
    },
  },
  /** The batch page (Block 1b, WP-1b.3, MF-INV-008; BLOCK-1B-BULK-DESIGN §11). */
  batch: {
    title: 'Prepare a batch',
    lead: 'Choose the task, then the documents, then review and run. Each document is handled on its own.',
    steps: {
      label: 'Batch steps',
      task: 'Task',
      documents: 'Documents',
      review: 'Review and run',
      needsInput: 'Needs your input',
    },
    tasks: {
      legend: 'What should the batch do?',
      sendHint:
        'For ready drafts and for issued documents not sent yet. No document is ever sent twice.',
      pdf: 'Save PDF copies in one file',
      pdfHint: 'A ZIP file with a PDF copy of each document as it is now.',
      discard: 'Delete drafts',
      discardHint:
        'Drafts only. Each number stays and is never reused; you have 6 seconds to undo.',
    },
    fromResults: 'From the list results',
    criteria: (criteria: string) => `Criteria: ${criteria}`,
    changeCriteria: 'Change the criteria in the list',
    noTask: 'Choose the task first: it decides which documents show here.',
    preview: { caption: 'List results this task applies to' },
    putAll: 'Add every result, up to 100 documents',
    putAllDone: (n: string) => `Added to the batch: ${n}.`,
    full: 'The batch is full. The limit is 100 documents.',
    trimmed: (n: string) =>
      `${n} documents match, so the batch took what it had room for, in the current order. Run this batch, then prepare the next.`,
    candidatesEmpty: 'No documents here that this task applies to',
    candidatesEmptyBody: 'Change the criteria in the list, or choose another task.',
    oneDocument: 'One document',
    addField: 'Add a document to the batch',
    rail: {
      title: 'The batch',
      task: (task: string) => `Task: ${task}`,
      room: (n: string) => `Room left: ${n} of 100`,
      empty: 'The batch is empty. Add every result, or one document.',
      takeOut: 'Take out',
      takeOutNamed: (number: string) => `Take ${number} out of the batch`,
      clear: 'Empty the batch',
      clearTitle: 'Empty the batch?',
      clearBody: 'Every document leaves the batch; no document changes.',
      clearConfirm: 'Empty it',
    },
    review: {
      title: 'Check the batch before you run it',
      checking: 'Checking each document…',
      outcome: 'What will happen',
      willRun: 'Will run',
      willSkip: (reason: string) => `Will be skipped: ${reason}`,
      summary: (run: string, skip: string) => `${run} will run, ${skip} will be skipped.`,
      total: (amount: string) => `Total to send, in EGP: ${amount}`,
      dropSkipped: 'Take out the ones to be skipped',
      nothing: 'Nothing will run: every document in the batch would be skipped.',
    },
    run: {
      send: 'Issue and send the batch',
      pdf: 'Save the batch file',
      discard: 'Delete the batch drafts',
    },
    confirm: {
      sendTitle: 'Issue and send the batch?',
      sendBody:
        'Each document is issued and sent on its own. An issued document cannot be taken back; anything the task does not apply to is skipped.',
      documents: 'Documents to run',
      discardTitle: 'Delete the batch drafts?',
      discardBody:
        'Each draft is deleted on its own; its number stays and is never reused. You have 6 seconds to undo.',
    },
    undo: 'The batch drafts will be deleted in a moment.',
    leaveGuard: 'The file is being prepared. If you leave now, it will not be saved.',
    progress: 'Running on the server…',
    progressFile: (done: string, total: string) => `Preparing the file: ${done} of ${total}`,
    results: {
      title: 'Batch result',
      summary: (done: string, skipped: string, failed: string, waiting: string) =>
        `Result: ${done} completed, ${skipped} skipped, ${failed} not completed, ${waiting} with ETA now.`,
      done: 'Completed',
      skipped: 'Skipped',
      failed: 'Not completed',
      code: 'Result code',
      outcome: 'What happened',
      next: 'Next step',
      following: "This page follows the Tax Authority's answer for each document.",
      lost: "Following stopped before every result arrived. Each row shows the document's state now.",
      unconfirmed: (state: string) => `Result not confirmed; its state now: ${state}`,
      unconfirmedCount: (n: string) =>
        `${n} not confirmed: each of those rows shows its document as it is now.`,
      retry: 'Try the not-completed ones again',
      new: 'Start a new batch',
      back: 'Back to billing documents',
      saveAgain: 'Save the file again',
      openDoc: 'Open the document',
      finishDraft: 'Finish it in the editor',
    },
    /** RIB-01 (DEC-046): leaving results that are not settled asks first. */
    leave: {
      title: 'Leave these results?',
      body: 'Some documents in these results are still with the Tax Authority, not completed, or not confirmed. No document changes, but this page will stop following them.',
      confirm: 'Leave the results',
    },
    lockedByRun:
      'This batch holds the results of a run, so its documents stay as they are. To prepare other documents, start a new batch.',
    retryTitle: 'Try the not-completed ones again?',
    retryBody: 'The task runs on each document as it is now.',
    /** RIM-07: shown only where the table is in view beside the dialog (not on phones). */
    retryPointer: 'What will happen to each one is marked in the table.',
    reasons: {
      notFound: 'Not found, or not in this organization',
      sentBefore: 'It already has an ETA state; it is never sent twice',
      notDraft: 'Not a draft',
      notSendable: 'Cannot be sent at its current stage',
      incomplete: 'The draft is incomplete',
      permission: 'You may not run this task on this document',
      changed: 'Changed after you reviewed it',
      ourSide: 'Could not run because of a fault on our side',
      etaAnswer: (answer: string) => `Tax Authority answer: ${answer}`,
      notReached: 'No result arrived, and the document has not changed',
    },
    errors: { tooMany: 'The limit is 100 documents per batch.' },
    announce: {
      put: (number: string) => `${number} is in the batch now.`,
      out: (number: string) => `${number} left the batch.`,
      finished: 'The run has finished.',
    },
  },
  receipts: {
    eyebrow: 'Billing',
    title: 'E-receipts',
    newReceipt: 'Start an e-receipt',
    newReturn: 'Start a no-reference return',
    startMenu: 'Start a receipt',
    emptyTitle: 'No receipts yet',
    emptyBody: 'Create your first receipt; it is numbered as soon as you save it.',
    noResultsTitle: 'Nothing matches',
    noResultsBody: 'Try another search, or clear the filters.',
    search: 'Search receipts',
    searchHint: 'Receipt number or buyer',
    filter: 'Narrow down',
    filterTitle: 'Narrow down receipts',
    filterCount: (n: number) => `Narrow down (${n})`,
    filterStatus: 'Receipt stage',
    filterTaxStatus: 'ETA status',
    filterType: 'Receipt type',
    filterAllTypes: 'All types',
    clearFilter: 'Clear all',
    applyFilter: 'Show results',
    columns: {
      number: 'Receipt number and kind',
      buyer: 'Buyer',
      date: 'Receipt date',
      amount: 'Receipt total',
      status: 'Stage and ETA status',
      updated: 'Last change',
    },
    sortBy: (column: string) => `Sort by ${column}`,
    noBuyer: 'No buyer yet',
    /** An issued receipt whose buyer was not identified (ETA buyer type P without a name). */
    anonymousBuyer: 'Unidentified buyer',
    notAccepted: 'Not accepted at submission',
    updating: 'Updating…',
    findLabel: 'Find and narrow down',
    rowActions: (number: string) => `Actions for ${number}`,
    open: 'Open',
    issueSend: 'Issue and send',
    editDraft: 'Edit draft',
    latestLine: (number: string) => `Last updated: ${number}`,
    confirm: {
      title: (number: string) => `Make receipt ${number} final at the Tax Authority?`,
      body: 'After issuing, the receipt can no longer be edited, and it takes the next place in the device’s chain of receipts.',
      noAmounts: 'The amounts cannot be calculated yet; issuing will say what is missing.',
    },
    /** Our own labels for the official ETA payment-method codes (snapshot 2026-10-03). */
    paymentMethods: {
      C: 'Paid in cash',
      V: 'Visa card',
      CC: 'Cash, with a contractor',
      VC: 'Visa card, with a contractor',
      VO: 'Purchase vouchers',
      PR: 'Promotional offer',
      GC: 'Gift card',
      P: 'Loyalty points',
      O: 'Another method',
    } as Record<string, string>,
    /**
     * Our own labels for the ETA receipt-type codes (v1.2, compliance review §9.1). The sector names
     * are ETA's own (LABELS §3), never the competitor's glosses (WP-8.9).
     */
    types: {
      s: 'Sale receipt',
      r: 'Return with reference',
      RWR: 'No-reference return',
      SC: 'Coffee and restaurant sale',
      RC: 'Coffee and restaurant return',
      SS: 'General services sale',
      RS: 'General services return',
      SR: 'Retail sale',
      RR: 'Retail return',
      ST: 'Transportation sale',
      RT: 'Transportation return',
      SB: 'Banking sale',
      RB: 'Banking return',
      SE: 'Education sale',
      RE: 'Education return',
      SP: 'Professional sale',
      RP: 'Professional return',
      SH: 'Shipping sale',
      RH: 'Shipping return',
      SN: 'Entertainment sale',
      RN: 'Entertainment return',
      SU: 'Utilities sale',
      RU: 'Utilities return',
    },
    editor: {
      newTitle: 'Start an e-receipt',
      returnTitle: 'No-reference return',
      choose: 'Choose from the list',
      noPermission: 'You may not create or edit receipts. Ask whoever runs the organization.',
      backToList: 'Back to e-receipts',
      correctionOf: (number: string) =>
        `Correcting ${number}: review it, then save. It takes a new number at its first save; ${number} stays as it is.`,
      returnOf: (number: string) =>
        `Return of ${number}: keep the returned lines and quantities, then save.`,
      editTitle: (number: string) => `Editing ${number}`,
      numberProvisional: 'Given when you save',
      numberNext: (n: string) => `Next number: ${n}`,
      issuedFromTitle: 'Issued from',
      receiptTitle: 'This receipt',
      linesTitle: 'Receipt lines',
      type: 'Receipt type',
      typeLocked: 'A saved receipt keeps its type.',
      pos: 'POS device',
      posHint: 'The device sets the issuing branch and the receipt’s number series.',
      noDevice:
        'No POS device is in service in this organization yet, so a receipt cannot be saved until one is registered.',
      noDeviceStaff:
        'No POS device is in service in this organization yet. Ask an administrator to register a POS device so receipts can be saved.',
      missingPos: 'Choose the POS device.',
      missingPosNone: 'No POS device is in service.',
      missingType: 'Choose the receipt type.',
      branch: 'Issuing branch',
      branchFromDevice: 'Follows the chosen POS device.',
      branchNone: 'Shown once a device is chosen.',
      branchCode: (code: string) => `ETA branch code ${code}`,
      activityCode: 'Taxpayer activity code',
      currency: 'Receipt currency',
      exchangeRate: 'Rate to EGP',
      rateHint: (currency: string) => `EGP for 1 ${currency}, up to 5 decimals.`,
      issueDate: 'Receipt date',
      issueDateHint: 'Leave empty to use the moment you issue.',
      paymentMethod: 'How it is paid',
      paymentMethodHint: 'From the Tax Authority’s official list.',
      returnTitleField: 'Return header',
      referenceUuid: 'Sale receipt being returned',
      referenceChosen: 'The chosen sale:',
      referenceUuidHint:
        'Search by receipt number, date (YYYY-MM-DD) or buyer. Only valid sale receipts are listed.',
      referenceUuidRequired: 'Choose the sale receipt being returned.',
      documentUseReason: 'Reason for the return',
      useReasons: {
        I: 'International return receipt',
        B: 'Sale issued before adoption',
      },
      salesIssuedDateTime: 'Original sale date',
      salesIssuedDateTimeHint: 'The date the original sale receipt was issued.',
      rwrMandatory: 'A return without reference needs both the reason and the original sale date.',
      rwrMissingReason: 'Choose the reason for the return.',
      rwrMissingDate: 'Enter the original sale date.',
      buyerTitle: 'Buyer',
      buyerType: 'Buyer category',
      buyerTypes: {
        B: 'Business in Egypt',
        P: 'Natural person',
        F: 'Foreign party',
      },
      buyerId: 'Buyer identifier',
      buyerName: 'Buyer name',
      buyerMobile: 'Buyer mobile',
      item: 'Product or service',
      quantity: 'Qty sold',
      unitPrice: (currency: string) => `Unit price (${currency})`,
      addLine: 'Add a line',
      removeLine: 'Remove line',
      noLines: 'No lines yet. Add the first line.',
      summaryTitle: 'Amounts as they will be issued',
      summaryNote:
        'Calculated by the server now, the same way the receipt will be issued; they update as you edit.',
      summaryEmpty: 'Add a line to see the amounts.',
      summaryIncomplete: 'The amounts appear once the lines are complete.',
      summaryUpdating: 'Calculating…',
      linePath: (n: string, field: string) => `Line ${n}: ${field}`,
      lineTaxes: 'Taxes on this line',
      taxRate: (rate: string) => `at ${rate}%`,
      feeAmount: (name: string, currency: string) => `${name} amount (${currency})`,
      feeAmountHint: (code: string) => `A fixed amount for the whole line, no rate · ${code}`,
      saveDraft: 'Save and finish later',
      saving: 'Saving…',
      saved: 'Draft saved.',
    },
    detail: {
      title: (number: string) => `E-receipt ${number}`,
      valuesTitle: 'Receipt data',
      linesTitle: 'Lines',
      taxesTitle: 'Tax totals',
      totalsTitle: 'Totals',
      etaTitle: 'Tax Authority',
      historyTitle: 'History',
      number: 'Number',
      type: 'Kind',
      issueDate: 'Receipt date',
      issuedAt: 'Issued at',
      currency: 'Receipt currency',
      exchangeRate: 'Rate to EGP',
      activity: 'Taxpayer activity code',
      pos: 'POS device',
      branch: 'Issuing branch',
      paymentMethod: 'How it is paid',
      buyerType: 'Buyer category',
      buyerId: 'Buyer identifier',
      buyerName: 'Buyer name',
      documentUseReason: 'Return reason',
      salesIssuedDateTime: 'Original sale date',
      referenceUuid: 'Reference receipt UUID',
      referenceReceipt: 'Referenced sale receipt',
      uuid: 'Receipt UUID',
      previousUuid: 'Previous receipt on this device',
      previousNone: 'This is the first receipt on this device.',
      previousAtIssue: 'Linked to the device’s last receipt when it is issued.',
      chainHead: 'Last receipt on this device',
      createdAt: 'Created',
      readOnly: 'Issued receipts are read-only.',
      draftNote:
        'The amounts below are calculated now as they will be issued, and are fixed when the receipt is issued.',
      draftNoAmounts: 'The amounts appear once the lines are complete.',
      notFoundTitle: 'We could not find this receipt',
      notFoundBody: 'The link may be mistyped, or the receipt belongs to another organization.',
      notAcceptedQualifier: 'not accepted at submission, so recorded as invalid',
      reasonNotAccepted:
        'The Tax Authority did not accept the receipt when it was sent, so its status there is Invalid.',
      reasonInvalid: 'The Tax Authority checked the receipt and found it invalid.',
      reasonCancelled: 'A Tax Authority reviewer cancelled the receipt after accepting it.',
      reasonLive: 'The receipt is with the Tax Authority now; this page updates on its own.',
      reasonUnknownCode:
        'The Tax Authority gave no reason we recognise; the technical details are below.',
      reasonStep: 'Check it did not pass:',
      reasonCodes: {
        'MOCK-NOT-ACCEPTED':
          'A deliberate simulated refusal in the test environment; nothing reaches the Tax Authority.',
        'MOCK-INVALID':
          'A deliberate simulated result in the test environment; nothing reaches the Tax Authority.',
        BadStructure: 'The receipt as sent does not have the structure the Tax Authority requires.',
        MaximumSizeExceeded: 'The submission was larger than the Tax Authority accepts.',
        DuplicateSubmission:
          'The same request was sent moments ago; the platform checks its result before any resend.',
      } as Record<string, string>,
      techDetails: 'Technical details',
      correctAction: 'Start a corrected receipt',
      correctHint:
        'Opens a new draft with the same data to correct and issue; this receipt stays as it is.',
      returnAction: 'Start a return of this receipt',
      eventReasons: {
        'HTTP 429': 'The Tax Authority asked us to wait; the request was retried automatically.',
        'HTTP 503':
          'The Tax Authority was briefly unavailable; the request was retried automatically.',
      } as Record<string, string>,
      taxType: 'Tax category',
      taxAmount: 'Tax amount',
      totalSales: 'Sales value',
      netAmount: 'Net amount',
      totalAmount: 'Receipt total',
      lineTotal: 'Line total',
      etaStatus: 'ETA status',
      etaReason: 'Reason',
      etaEnvironment: 'Environment',
      etaUuid: 'ETA receipt id',
      etaSyncedAt: 'Last checked',
      issuing: 'Issuing…',
      issuedToast: (number: string) => `${number} issued and sent.`,
      events: {
        draft_created: 'Draft created',
        draft_saved: 'Draft saved',
        issued: 'Receipt issued',
        eta_submission_requested: 'Sent to the Tax Authority',
        eta_accepted_for_validation: 'Accepted for validation',
        eta_valid: 'Valid',
        eta_invalid: 'Invalid',
        eta_rejected: 'Rejected by the receiver',
        eta_cancelled: 'Cancelled at ETA',
        eta_submission_rejected: 'Not accepted at submission',
        eta_outcome_unknown: 'No answer yet — checking with ETA',
        eta_reconciled: 'Found at ETA',
        eta_retry_scheduled: 'ETA busy — retrying',
      } as Record<string, string>,
    },
  },
  editor: {
    newTitle: 'New invoice',
    newExportTitle: 'New export invoice',
    editTitle: (number: string) => `Editing ${number}`,
    number: 'Number',
    numberProvisional: 'Given when you save',
    numberNext: (n: string) => `Next number: ${n}`,
    sections: {
      customer: 'Buyer',
      lines: 'Invoice lines',
      totals: 'Summary',
      additional: 'Order references',
      issuer: 'Issued by',
      readiness: 'Before you issue',
    },
    customer: {
      search: 'Buyer',
      placeholder: 'Search by name, tax ID or phone',
      newCustomer: 'New buyer',
      cash: 'Unidentified buyer',
      cashHint:
        'A person without an ID. The Tax Authority applies a threshold it does not publish.',
      change: 'Change buyer',
      edit: 'Edit buyer details',
      details: 'Show details',
      hideDetails: 'Hide details',
      none: 'Pick a buyer, add a new one, or bill an unidentified buyer.',
    },
    lines: {
      addItems: 'Pick from catalogue',
      addItemsHint: 'Search your catalogue and pick several items at once.',
      empty: 'This invoice has no lines yet. Choose from your item catalogue.',
      item: 'Product or service',
      quantity: 'Qty sold',
      unitPrice: (currency: string) => `Unit price (${currency})`,
      fromCatalogue: 'from the catalogue',
      discount: 'Discount (EGP)',
      discountPercent: '%',
      discountAmount: 'EGP',
      discountMode: 'Discount as',
      itemsDiscount: 'Items discount (EGP)',
      valueDifference: 'Value difference (EGP)',
      taxes: 'Line taxes',
      noTaxes: 'No tax',
      addTax: 'Apply another tax',
      removeTax: (name: string) => `Remove ${name}`,
      fixedAmount: (name: string) => `${name} amount (EGP)`,
      total: 'Line total',
      remove: 'Remove line',
      more: 'More options',
      less: 'Fewer options',
      weightUnit: 'Weight unit',
      weightQuantity: 'Weight',
      editItem: 'Edit item',
      lineLabel: (n: number) => `Line ${n}`,
    },
    totals: {
      salesTotal: 'Sales value',
      discount: 'Line discounts',
      net: 'Value after discounts',
      itemsDiscount: 'Post-tax item discounts',
      extraDiscount: 'Document-level discount (EGP)',
      total: 'Document total',
      amountDue: 'Due from buyer (EGP)',
      amountDueForeign: (currency: string) => `Due from buyer in ${currency} (display only)`,
      // Credit-note direction (Block 11, WP-11.4; role-2 ruling R-2): a credit note returns value
      // to the buyer, so its due box reads «المستحق للمشتري» / "Due to buyer", matching the PDF.
      amountDueCredit: 'Due to buyer (EGP)',
      amountDueCreditForeign: (currency: string) => `Due to buyer in ${currency} (display only)`,
      deducted: 'deducted',
      egpNote: 'All amounts except the unit price are in EGP.',
      waiting: 'Totals appear when the lines are complete.',
    },
    settings: {
      issueDate: 'Document date',
      issueDateHint: 'Leave empty to use the moment you issue.',
      dueDate: 'Payment due by',
      type: 'Where it is sold',
      local: 'Domestic',
      export: 'For export',
      exportHint: 'Export invoices need a foreign buyer, weights on every line and a service date.',
      currency: 'Document currency',
      exchangeRate: 'Rate to EGP',
      rateHint: (currency: string) => `EGP for 1 ${currency}, up to 5 decimals.`,
      activityCode: 'Taxpayer activity code',
      branch: 'Issuing branch',
      serviceDate: 'Service delivered on',
      typeLocked: 'Saved documents keep their type family.',
    },
    additional: {
      poRef: "Buyer's PO number",
      poDesc: 'Purchase order details',
      soRef: 'Our sales order number',
      soDesc: 'Sales order details',
      proforma: 'Proforma reference',
    },
    readiness: {
      ready: 'Ready to issue',
      incomplete: (n: number) => (n === 1 ? '1 thing to fix' : `${n} things to fix`),
      localOnly:
        'These are local checks. The Tax Authority validates the invoice after it is sent.',
      // Block 11 (role-4 m-1): a note's readiness line names the note, not the invoice.
      localOnlyNote:
        'These are local checks. The Tax Authority validates the note after it is sent.',
      warnings: 'Worth a look',
      goTo: 'Go to field',
    },
    saveDraft: 'Save and finish later',
    saving: 'Saving…',
    saved: 'Draft saved.',
    savedAt: (time: string) => `Saved ${time}`,
    unsaved: 'Unsaved changes',
    issueSend: 'Issue and send',
    conflictTitle: 'Someone else changed this draft',
    conflictBody: 'Your edits are still here. Load the latest version, or copy your edits first.',
    loadLatest: 'Load the latest version',
    confirm: {
      title: 'Issue and send to the Tax Authority?',
      body: 'After issuing, the invoice can no longer be edited or deleted.',
      /** Block 11 (m-1): note-aware wording of the issue confirmation (not "the invoice"). */
      titleNote: 'Issue the note and send it to the Tax Authority?',
      bodyNote: 'After issuing, the note can no longer be edited or deleted.',
      organization: 'Issuer',
      environment: 'Environment',
      customer: 'Buyer',
      number: 'Number',
      total: 'Document total',
      confirm: 'Issue and send',
      /** WP-1b.4: the buyer-copy row of the confirmation summary (its term is `editor.mail.title`). */
      mailSend: (email: string) => `To ${email}, once the Tax Authority accepts it`,
      mailNone: 'No e-mail copy',
      mailTest: 'a test message saved to a file',
      mailFromEditor:
        "To e-mail the buyer a copy, issue the invoice from the editor's review step.",
      openReview: 'Open the review step',
    },
    /** WP-1b.4 (MF-INV-010): the step-4 block «نسخة للمشتري بالبريد». */
    mail: {
      title: 'A copy for the buyer by e-mail',
      lead: 'We can e-mail the buyer a PDF of the invoice once it is issued. Nothing goes out unless you choose it here.',
      none: "Don't send a copy",
      noneHint: 'The invoice is issued and goes to the Tax Authority only.',
      send: 'E-mail a PDF copy to this address',
      sendHint:
        'It goes out once the Tax Authority accepts the invoice; follow it on the invoice page.',
      missing:
        'This buyer has no e-mail for invoices, so the invoice will be issued without an e-mail copy.',
      invalid:
        "The e-mail for invoices on this buyer's record is not a valid address, so the invoice will be issued without an e-mail copy.",
      cash: 'An unidentified buyer has no address to send a copy to.',
      noBuyer: 'Choose the buyer first, then come back here to decide on a copy.',
      fix: 'Add or correct the address in the buyer step',
      change: 'Change the address in the buyer step',
      testNote:
        'Test environment: the message will not reach the buyer; it is saved to a file on the server.',
      reset: "The buyer's address changed, so the choice went back to not sending.",
    },
    issued: (number: string) => `${number} issued and sent.`,
    copiedFrom: (number: string) => `Copied from ${number}.`,
    pricesCleared: 'Currency changed: enter the unit prices in the new currency.',
    copyWarnings: 'Check these before issuing:',
    /** A correction draft (WP-1b.2): what it replaces, and what the Tax Authority reported on it. */
    supersedesBanner: 'This correction draft replaces an invoice the Tax Authority did not accept:',
    supersedesHint:
      'Fix what the Tax Authority reported, then issue it; that invoice is then marked replaced.',
    supersedesReasons: (number: string) => `What the Tax Authority reported on ${number}`,
    leaveWarning: 'You have unsaved changes.',
    steps: {
      label: 'Invoice steps',
      counter: (n: string, total: string) => `Step ${n} of ${total}`,
      clean: 'Nothing to fix',
      announce: (n: string, total: string, name: string) => `Step ${n} of ${total}: ${name}`,
      details: 'Document details',
      review: 'Review and issue',
      reference: 'Reference document',
      noteLines: 'Note lines',
    },
    groups: {
      dates: 'Dates',
      money: 'Document currency and rate',
    },
    back: 'Back',
    next: (step: string) => `Continue to ${step}`,
    review: {
      title: 'Check the invoice before you issue it',
      // Block 11 (role-4 m-1): a note's step-4 heading names the note, not the invoice.
      titleNote: 'Check the note before you issue it',
      backTo: (step: string) => `Back to ${step}`,
      clean: 'The checks found nothing that blocks issuing.',
      testingOnly: 'Testing only',
    },
    buyerChoice: {
      label: 'Who is the buyer?',
      records: 'From your buyer records',
      newHint: 'A company in Egypt, a private buyer or a party abroad; kept in your buyer records.',
    },
    summary: {
      label: 'Invoice at a glance',
      /** Block 11 (m-1): the summary-rail heading on a note editor. */
      labelNote: 'Note at a glance',
    },
    /** Credit and debit note editor (Block 11, WP-11.4; notes-editor.md:27-110). */
    note: {
      createCredit: 'Create a credit note',
      createDebit: 'Create a debit note',
      createExportCredit: 'Create an export credit note',
      createExportDebit: 'Create an export debit note',
      rule: 'A note is built on one or more valid invoices issued to the same buyer.',
      addField: 'Add a reference invoice',
      addHint: 'Search by invoice number, buyer name or registration number',
      /** Block 11 (m-2): the search hint of the reference-lines sheet (lines, not invoices). */
      addLineHint: 'Search the reference lines by item or code',
      takeOut: 'Take out',
      buyer: (name: string) => `Buyer: ${name}`,
      fromReference: 'From the reference invoice',
      fromReferenceField: 'Taken from the reference invoice',
      fromInvoice: (invoice: string) => `From ${invoice}`,
      fromLine: (line: number) => `line ${line}`,
      referencedLine: 'Referenced line',
      addLine: 'Add a line from the reference',
      linesToNote: (n: number) =>
        n === 1 ? 'Add 1 line to the note' : `Add ${n} lines to the note`,
      noReference: 'Choose the reference document first',
      backToReference: 'Back to the reference document',
      stillReducible: 'Still reducible',
      reducibleBefore: 'Before this note',
      reducibleAfter: 'After this note',
      needsCancelPermission:
        'Issuing a credit note needs the cancel-at-the-Tax-Authority permission',
    },
  },
  detail: {
    title: (number: string) => `E-invoice ${number}`,
    exportTitle: (number: string) => `Export e-invoice ${number}`,
    noteCreditTitle: (number: string) => `Credit note ${number}`,
    noteDebitTitle: (number: string) => `Debit note ${number}`,
    kindLocal: 'Domestic e-invoice',
    kindExport: 'Export e-invoice',
    kindCredit: 'Domestic e-credit note',
    kindDebit: 'Domestic e-debit note',
    kindExportCredit: 'Export e-credit note',
    kindExportDebit: 'Export e-debit note',
    sections: {
      document: 'Document',
      settings: 'Properties',
      activity: 'History',
      eta: 'Tax Authority',
    },
    seller: 'Seller',
    buyer: 'Buyer',
    issuedAt: 'Issued at',
    issueDate: 'Document date',
    dueDate: 'Payment due by',
    serviceDate: 'Service delivered on',
    createdAt: 'Created',
    taxId: 'Tax ID',
    nationalId: 'National ID number',
    foreignId: 'Foreign tax ID',
    type: 'Document kind',
    lines: 'Lines',
    amountDue: 'Due from buyer',
    amountDueEgp: 'Due from buyer (EGP)',
    amountDueForeign: (currency: string) => `Due from buyer (${currency})`,
    // Credit-note direction (Block 11, WP-11.4; role-2 ruling R-2), matching the PDF.
    amountDueCredit: 'Due to buyer',
    amountDueCreditEgp: 'Due to buyer (EGP)',
    amountDueCreditForeign: (currency: string) => `Due to buyer (${currency})`,
    currency: 'Document currency',
    exchangeRate: 'Rate to EGP',
    activity: 'Taxpayer activity',
    branch: 'Issuing branch',
    /** Related-documents bands and note start actions (Block 11, WP-11.4; sales-notes.md §6). */
    referenceDocuments: 'Reference documents',
    notesOnInvoice: 'Notes on this invoice',
    stillReducible: 'Still reducible',
    createCreditFromInvoice: 'Create a credit note from this invoice',
    createDebitFromInvoice: 'Create a debit note from this invoice',
    deleteTitle: 'Delete this draft?',
    deleteBody: (number: string) =>
      `${number} will show as a deleted draft and its number is never reused. You can undo for a few seconds.`,
    deleteConfirm: 'Delete draft',
    copiedFromBanner: (number: string) => `This draft was copied from ${number}.`,
    copiesLabel: 'Copies',
    /** WP-1b.2: a refused invoice and its replacement link to each other. */
    supersededBanner: 'This invoice was replaced by a corrected invoice:',
    supersedesBanner: 'This invoice replaces an invoice the Tax Authority did not accept:',
    /** The Tax Authority's verdict on a refused document, field by field (WP-1b.2, MF-INV-009). */
    verdict: {
      title: "The Tax Authority's validation result",
      lead: "These are the Tax Authority's reasons, as it sent them, field by field. Our own checks before issue are separate.",
      leadSubmission:
        'The Tax Authority did not accept this document when it was sent, so it never reached validation.',
      steps: "The Tax Authority's validation steps",
      submission: 'Why it was not accepted when sent',
      passed: 'Passed',
      failed: 'Did not pass',
      field: 'Field',
      code: 'Authority code',
      noDetail: 'The Tax Authority sent no detail for this step.',
      correct: 'Correct and resend',
      correctHint:
        'Creates a new draft with its own number and the same buyer, lines and settings. This invoice stays as it is until you issue the new draft; it is then marked replaced.',
      continue: (number: string) => `Continue the correction in ${number}`,
      openDraft: 'A correction draft is open:',
      noPermission: 'Creating the correction draft needs the permission to create drafts.',
      replaced: 'Corrected and replaced by:',
    },
    eta: {
      uuid: 'ETA UUID',
      longId: 'Long ID',
      environment: 'Environment',
      reason: 'Reason',
      submissions: 'Submission history',
      attempts: (n: number) => (n === 1 ? '1 attempt' : `${n} attempts`),
      steps: 'Validation steps',
      notSent: 'Not sent to the Tax Authority yet.',
      watching: 'ETA may still report a rejection or cancellation for a limited time.',
      submissionStatus: {
        queued: 'Queued',
        claimed: 'Preparing',
        sending: 'Transmitting',
        accepted: 'Taken for validation',
        partially_accepted: 'Partly accepted',
        rejected: 'Not accepted',
        retry_wait: 'Waiting to retry',
        unknown: 'Checking with ETA',
        failed: 'Could not be sent',
      } as Record<string, string>,
    },
    actorSystem: 'System',
    events: {
      draft_created: 'Draft created',
      draft_saved: 'Draft saved',
      issued: 'Issued as final',
      discarded: 'Draft deleted',
      copied_from: 'Created as a copy',
      copied_to: 'Copied',
      eta_submission_requested: 'Sent to the Tax Authority',
      eta_accepted_for_validation: 'Accepted for validation',
      eta_valid: 'Validated: valid',
      eta_invalid: 'Validated: invalid',
      eta_rejected: 'Rejected by the receiver',
      // U-10 (W3-07; F-5, DEC-069): ETA's status alone does not settle a cancellation — the buyer may
      // still object inside their own period, which returns the document to valid. So the one event
      // became two, each true of its own read: this one while the objection period is not certainly
      // over, `eta_cancelled_final` once it is (`eta-watch.ts` `cancellationSettled`).
      eta_cancelled:
        'The Tax Authority reads it as cancelled — the buyer may object unless their objection period has ended',
      eta_cancelled_final:
        "The Tax Authority cancelled this document, and the buyer's objection period has ended",
      eta_submission_rejected: 'Not accepted at submission',
      eta_outcome_unknown: 'No answer from ETA — checking before any resend',
      eta_reconciled: 'Found at ETA (not sent again)',
      eta_retry_scheduled: 'ETA busy — retrying',
      correction_created: 'Created as a correction draft',
      correction_started: 'Correction started in a new draft',
      superseded: 'Replaced by a corrected invoice',
      supersedes: 'Issued in place of an invoice the Tax Authority did not accept',
      signed: 'E-seal applied',
      email_requested: 'E-mail copy for the buyer requested',
      email_sent: "Buyer's copy handed to the mail server",
      email_saved: "Buyer's copy saved as a test message",
      email_failed: "Buyer's copy could not be e-mailed",
      email_withheld: "Buyer's copy held back: the Tax Authority did not accept the invoice",
      email_retry: "Another try requested for the buyer's copy",
      // Block 11: the state changes at the Tax Authority and the dates it reports.
      eta_cancel_requested: 'Cancellation requested at the Tax Authority',
      eta_cancel_sending: 'Cancellation request going out',
      eta_cancel_done: 'The Tax Authority took the cancellation request',
      eta_cancel_refused: 'Cancellation request refused',
      eta_cancel_unknown: 'No answer to the cancellation request — checking, never resending',
      eta_cancel_not_applied: 'The cancellation request did not take effect',
      eta_cancel_failed: 'The cancellation request could not be sent',
      eta_cancel_confirmed: 'The Tax Authority confirmed the cancellation request',
      eta_cancel_unconfirmed: 'The Tax Authority has not confirmed the cancellation request yet',
      eta_cancel_release_requested: 'Asked to treat the cancellation request as not outstanding',
      // F-4: what the release establishes, and nothing more (R-17: ETA may still apply the cancel).
      // B-1 (DEC-107): the EN pair is "outstanding / not outstanding" with the verb "treat". ERP
      // English "release" is the approve-and-let-it-go-forward verb, so it carried the same
      // re-send misread as the Arabic «تحرير»; "stands" is taken by `cancelRequestDelayedDate`.
      eta_cancel_released:
        'The cancellation request treated as not outstanding after a read showed the document valid',
      // m-1: read alone in an export or a log, naming only the standing reads as neutral status;
      // the line has to say that what was asked did NOT happen. It does that by refusing the ACTION:
      // "was not treated as not outstanding" stacked two negatives where the Arabic sibling reads
      // naturally (role 4's second rating, 2026-10-07).
      eta_cancel_release_refused:
        'Treating the cancellation request as not outstanding was refused; it is still outstanding',
      eta_decline_rejection_requested: "Objection to the buyer's rejection requested",
      eta_decline_rejection_sending: 'Objection going out',
      eta_decline_rejection_done: 'The Tax Authority took the objection',
      eta_decline_rejection_refused: 'Objection refused',
      eta_decline_rejection_unknown: 'No answer to the objection — checking, never resending',
      eta_decline_rejection_not_applied: 'The objection did not take effect',
      eta_decline_rejection_failed: 'The objection could not be sent',
      eta_decline_rejection_confirmed: 'The Tax Authority confirmed the objection',
      eta_decline_rejection_unconfirmed: 'The Tax Authority has not confirmed the objection yet',
      eta_cancel_request_reported: 'The Tax Authority reported a cancellation request',
      eta_reject_request_reported: "The Tax Authority reported the buyer's rejection",
      eta_cancel_declined: 'The buyer objected to the cancellation',
      eta_rejection_declined: 'The Tax Authority recorded the objection to the rejection',
    } as Record<string, string>,
    /** WP-1b.4: the detail section of the buyer's e-mail copy; its state is apart from the stage. */
    delivery: {
      title: "The buyer's e-mail copy",
      waitingEta: 'Waiting for the Tax Authority to accept the invoice.',
      none: 'No e-mail copy was requested when this invoice was issued.',
      // Block 11 (role-4 m-1): on a note's detail page the band names the note, not the invoice.
      noneNote: 'No e-mail copy was requested when this note was issued.',
      recipient: "Buyer's e-mail",
      attempts: (n: string) => `Attempts: ${n}`,
      sentAt: (time: string) => `Handed over at ${time}`,
      /** RIM-01: the file transport saved it; nothing was handed over. */
      savedAt: (time: string) => `Saved at ${time}`,
      lastAttempt: (time: string) => `Last try: ${time}`,
      nextAttempt: (time: string) => `Another automatic try at about ${time}`,
      reply: "The mail server's reply",
      retry: 'Try sending again',
      retried: 'The copy is back in the mail queue.',
      /** RIM-03: the file type is rendered as an isolated code. */
      testNote: (file: string) =>
        `Test environment: the message never left the server; it was saved there as an ${file} file.`,
      status: {
        queued: 'In the mail queue',
        sending: 'Going out now',
        sent: 'Handed to the mail server',
        saved: 'Saved as a test message',
        failed: 'Could not be e-mailed',
        withheld: 'Held back: the Tax Authority did not accept the invoice',
      },
    },
    journey: {
      label: 'Document journey',
      drafting: 'Drafting',
      finalIssue: 'Final issue',
      etaValidation: 'ETA validation',
      result: 'Result',
      notReached: 'Not reached yet',
      notApplicable: 'Not applicable',
      /** Block 11: stage 4 while a request is at the Tax Authority, and after the other side's answer. */
      cancelInProgress: 'Cancellation requested',
      declineInProgress: 'Objection requested',
      cancelDeclined: 'Valid: the buyer objected to the cancellation',
      rejectionDeclined: 'Valid again: our objection was recorded',
    },
    glance: 'At a glance',
    /**
     * Block 11 (BLOCK-11-PLAN D-3, D-5; document-cancellation §6): the «إجراءات لدى المصلحة» band, its
     * dialogs and its lines. ETA's parameter names, codes and reported dates stay Latin and isolated.
     */
    actions: {
      title: 'Actions at the Tax Authority',
      cancelPeriod: 'Cancellation period',
      declinePeriod: 'Objection period',
      open: (date: string) => `Open until ${date}`,
      uncertain:
        'The period may have ended; the Tax Authority decides when it receives the request',
      closed: (date: string) => `The period ended on ${date}`,
      closedNoDate: 'The period has ended',
      unknown: (elapsed: string, event: string) =>
        `The period is limited and set by the Tax Authority; act promptly. ${elapsed} since ${event}.`,
      sinceSubmission: 'the submission to the Tax Authority',
      sinceIssue: 'the final issue',
      sinceRejection: "the buyer's rejection",
      elapsed: {
        minutes: (n: number) => (n === 1 ? '1 minute' : `${n} minutes`),
        hours: (n: number) => (n === 1 ? '1 hour' : `${n} hours`),
        days: (n: number) => (n === 1 ? '1 day' : `${n} days`),
      },
      simulatorValue: 'Simulator value',
      reports: 'The Tax Authority reports:',
      noParameters: 'The Tax Authority reported no periods for this kind of document.',
      noParametersForAction: 'The Tax Authority reported no period for this action.',
      validFrom: 'in effect from',
      validTo: 'until',
      validOpen: 'no end date',
      dates: 'Dates reported by the Tax Authority',
      dateLabels: {
        cancelRequestDate: 'Cancellation request',
        cancelRequestDelayedDate: 'The cancellation stands unless objected to by',
        rejectRequestDate: "Buyer's rejection",
        rejectRequestDelayedDate: 'The rejection stands unless objected to by',
        declineCancelRequestDate: "Buyer's objection to the cancellation",
        declineRejectRequestDate: 'Our objection to the rejection',
      },
      checking: 'Checking with the Tax Authority',
      unconfirmed: 'Sent; the Tax Authority has not confirmed its effect yet',
      release:
        'No confirmation the cancellation took effect — treat the request as not outstanding',
      // F-4 (R-17): we stopped treating the request as outstanding and the Authority showed the
      // document valid. Whether ETA still applies the cancellation afterwards is not ours to assert.
      // B-1 (DEC-107): "treat" describes OUR handling, so it cannot be read as sending anything.
      released:
        'We no longer treat the cancellation request as outstanding; the Tax Authority showed the document valid',
      releaseRefused:
        'The cancellation request is still outstanding: the Tax Authority shows a cancellation request or a state other than valid',
      cancelRequested: 'The cancellation request is with the Tax Authority',
      // G-3 (F-5; plan D-3): see the Arabic dictionary — the band carries the same qualifier as the
      // timeline, from the one `cancellationSettled` predicate, and the keys are named for the state.
      cancelledSettled: 'The Tax Authority cancelled this document',
      cancelledPendingObjection:
        'The Tax Authority cancelled this document; the buyer may still object within their period',
      cancelDeclined: (date: string) =>
        `The buyer objected to the cancellation on ${date}; the document stays valid. A credit note remains the way to correct it.`,
      cancelDeclinedNoDate:
        'The buyer objected to the cancellation; the document stays valid. A credit note remains the way to correct it.',
      rejected: 'The buyer rejected this document at the Tax Authority',
      rejectionDeclined:
        'We objected to the rejection; the Tax Authority shows the document valid again',
      cancelClosed:
        'The cancellation period has ended. A credit note remains the way to correct this invoice.',
      declineClosed: 'The period to object to the rejection has ended; nothing can be sent.',
      frozen:
        'The Tax Authority has frozen this document, so it accepts no action (cancellation or objection) until the Tax Authority lifts the freeze; this program cannot lift it.',
      frozenWarning: 'The document is frozen at the Tax Authority; it may refuse the request',
      inFlight: 'Another request for this document is in progress at the Tax Authority',
      watchEnded: (date: string) => `Automatic follow-up stopped on ${date}`,
      check: 'Check with the Tax Authority now',
      checkRequested: 'Reading requested from the Tax Authority; the result will appear here',
      cancelAction: 'Ask the Tax Authority to cancel this document',
      declineAction: "Object to the buyer's rejection",
      requestOf: {
        cancel: 'Cancellation request',
        decline_rejection: 'Objection to the rejection',
        reject: 'Rejection',
        decline_cancellation: 'Objection to the cancellation',
      },
      refusedBy: {
        eta: 'refused by the Tax Authority',
        local: 'not sent',
      },
      notApplied: 'The Tax Authority did not apply the request',
      failed: 'The request could not be sent; nothing reached the Tax Authority',
      outcome: {
        eta_window_closed: "The Tax Authority's period for this action has ended",
        eta_incorrect_state: "The Tax Authority refused: the document's state does not allow it",
        eta_forbidden: "The organization's Tax Authority account does not allow this action",
        document_frozen: 'The Tax Authority has frozen this document',
        active_referencing_documents: 'First cancel the notes based on this invoice',
        eta_refused: 'The Tax Authority refused the request',
        eta_throttled: 'The Tax Authority asked us to slow down; nothing was sent twice',
        eta_auth_failed: 'The Tax Authority refused the connection credentials',
      } as Record<string, string>,
      refusal: {
        not_cancellable: "The document's state at the Tax Authority does not allow this now",
        not_rejected: "The document's state at the Tax Authority does not allow this now",
        request_in_flight: 'Another request for this document is in progress at the Tax Authority',
        already_requested:
          'This request was already sent; we are waiting for the Tax Authority to confirm its effect',
        already_declined: 'This was already answered once; it cannot be repeated',
        cancel_declined_by_receiver:
          'The buyer objected to the cancellation; a credit note remains the way to correct it',
        // m-2: a refusal can surface as a toast with the button out of view, so it names the
        // action instead of pointing at it with a bare "This".
        release_not_allowed:
          "Only a cancellation request awaiting the Tax Authority's confirmation can be treated as not outstanding",
        not_readable: 'There is nothing to read from the Tax Authority yet',
      } as Record<string, string>,
      // The next step after a refusal (RB3R11-06 / W3-05): what to change and where. The codes in
      // NO_RETRY (EtaActions.tsx) also hide the action, so the band never offers a doomed retry.
      nextStep: {
        eta_forbidden:
          'Next: ask whoever manages the organization’s Tax Authority account to enable this action; retrying with the current account will not succeed.',
        active_referencing_documents:
          'Next: first cancel the notes based on this invoice, then try again.',
        eta_auth_failed: 'Next: check the Tax Authority connection credentials, then try again.',
        document_frozen:
          'Next: wait for the Tax Authority to lift the freeze; this cannot be done now.',
      } as Record<string, string>,
      versionChanged: "The document's Tax Authority state changed; check it, then try again",
      cancelDialog: {
        title: 'Request a cancellation at the Tax Authority',
        lead: 'We send the Tax Authority a request to cancel this document. It is cancelled only if the Tax Authority accepts it, and the buyer may object within their own period.',
        number: 'Document number',
        buyer: 'Buyer',
        period: 'Period',
        reasonGroup: 'Reason for the cancellation',
        reasonBuyer: "The buyer's details are wrong",
        reasonDocument: "The document's details are wrong",
        reasonOther: 'Another reason',
        reasonText: 'The reason as the Tax Authority will receive it',
        reasonHelp: '1 to 500 characters, sent exactly as written',
        confirm: 'Send the cancellation request',
        queued: 'Cancellation request queued; the Tax Authority’s answer will show on this page',
        same: 'This cancellation request is already on its way',
      },
      declineDialog: {
        title: "Objection to the buyer's rejection",
        lead: "We send the Tax Authority your objection to the buyer's rejection. If it accepts it, the document is valid again. An objection is made only once.",
        confirm: 'Send the objection',
        queued: 'Objection queued; the Tax Authority’s answer will show on this page',
      },
      // G-1 (R-17): the lead invites a new cancellation or a credit note, which is exactly the case
      // R-17 names — a released cancel that ETA applies after all, with a credit note issued in
      // between. So it also says what would actually happen then, and the claim is true of the code:
      // `cancelPending` (`packages/domain/src/sales/state-change.ts`) is true again as soon as ETA
      // reports `cancelRequestDate` with no decline and a status other than `cancelled` — a released
      // request is not live — and `eta-watch.ts` writes that reported date into the state, so the
      // band's «طلب الإلغاء لدى المصلحة» line and the list's pending cancellation both return.
      releaseDialog: {
        title: 'Treat the cancellation request as not outstanding',
        lead: "We will first read the document's state from the Tax Authority. If it shows the document valid with no cancellation request, we treat the request as not outstanding on our side, and you can then request cancellation again or create a credit note. And if the Tax Authority reports a cancellation-request date after that, the request appears on this page again.",
        confirm: 'Treat the request as not outstanding',
        queued: 'Asked to treat the request as not outstanding; the reading will show on this page',
      },
    },
  },
  parties: {
    newTitle: 'New buyer',
    editTitle: 'Buyer details',
    type: 'Buyer category',
    types: {
      B: 'Business in Egypt',
      P: 'Natural person',
      F: 'Foreign party',
    },
    typeHints: {
      B: 'Registered company: 9-digit tax registration number.',
      P: 'Individual: the 14-digit national ID is optional.',
      F: 'Outside Egypt: tax ID in their country.',
    },
    name: 'Buyer name',
    email: 'E-mail for invoices',
    phone: 'Contact phone',
    taxIdB: 'Tax registration number',
    taxIdP: 'National ID number',
    taxIdF: 'Foreign tax ID',
    category: 'Customer group',
    noCategory: 'No group',
    newCategory: 'New group',
    categoryName: 'Group name',
    create: 'Create and select',
    save: 'Save buyer',
    created: 'Buyer added.',
    updated: 'Buyer details updated.',
  },
  catalogue: {
    pickerTitle: 'Pick from catalogue',
    pickerSearch: 'Item name, SKU or ETA code',
    selected: (n: number) => (n === 1 ? '1 selected' : `${n} selected`),
    addSelected: (n: number) => (n === 1 ? 'Add 1 item' : `Add ${n} items`),
    newItem: 'New catalogue item',
    editTitle: 'Edit item',
    newTitle: 'New catalogue item',
    nameAr: 'Arabic name',
    nameEn: 'English name',
    description: 'Description on the invoice',
    price: 'Catalogue unit price (EGP)',
    internalCode: 'SKU / internal code',
    unit: 'Selling unit',
    kgWarning: 'In the ETA unit table "KG" means keg. For kilograms choose KGM.',
    discount: 'Default discount (EGP)',
    itemsDiscount: 'Items discount (EGP)',
    valueDifference: 'Value difference (EGP)',
    etaCode: 'ETA item code',
    etaCodeHint: 'Search your registered codes or enter a new one (EGS or GS1).',
    etaType: 'Code type',
    codeSearch: 'Your codes',
    taxes: 'Default taxes',
    addCustomTax: 'Organization-specific tax',
    customTaxTitle: 'Organization-specific tax',
    taxType: 'ETA tax type',
    taxSubtype: 'Subtype',
    rate: 'Rate (%)',
    taxNameAr: 'Arabic name',
    taxNameEn: 'English name',
    createTax: 'Save this tax',
    classification: 'Classification (stored for reports only)',
    readOnlyNote: 'Set once when the item is created.',
    taxGoodsType: 'Tax goods type',
    taxGoodsTypes: { general: 'Standard', table: 'Table' },
    tableGoodsType: 'Table type',
    tableGoodsTypes: {
      none: 'Not a table item',
      table_1: 'First table',
      table_2: 'Second table',
    },
    statementType: 'Return classification',
    statementTypes: {
      goods: 'Products',
      service: 'Work and services',
      adjustment: 'Value corrections',
    },
    itemKind: 'Item kind',
    itemKinds: { goods: 'Tangible product', service: 'Intangible service' },
    create: 'Create and add',
    save: 'Save item',
    created: 'Item created.',
    updated: 'Item updated.',
    category: 'Item group',
    empty: 'No items match.',
    backToList: 'Back to the catalogue',
    linesAdded: (n: number) => (n === 1 ? '1 line added.' : `${n} lines added.`),
  },
  dashboard: {
    greeting: (name: string) => `Welcome, ${name}`,
    subtitle: 'Your invoicing at a glance.',
    recent: 'Recent documents',
    viewAll: 'View all invoices',
    newInvoice: 'Start an invoice',
    setupTitle: 'Get ready to issue invoices',
    setupBody: 'Complete these once. Each step is checked again when you issue.',
    setupDone: 'Setup complete',
    steps: {
      rin: 'Tax registration number',
      profile: 'Contact e-mail and phone',
      branch: 'Issuing branch address',
      activity: 'Taxpayer activity codes',
      tax: 'VAT and other taxes',
      customer: 'First buyer',
      item: 'First item',
    },
    stepAction: 'Set up',
    stepDone: 'Done',
  },
  setup: {
    profileTitle: 'Organization details',
    rin: 'Tax registration number',
    email: 'Billing e-mail',
    phone: 'Billing phone',
    save: 'Save details',
    saved: 'Saved.',
    branchTitle: 'Issuing branch',
    etaBranchId: 'ETA branch ID',
    etaBranchIdHint: 'Use 0 for the head office.',
    branchName: 'Your name for this branch',
    addBranch: 'Save branch',
    activityTitle: 'Taxpayer activity codes',
    activitySearch: 'Search the official activity codes',
    primary: 'Primary',
    makePrimary: 'Make primary',
    saveActivities: 'Save activity codes',
    activityLocked: 'Activity codes are set once.',
    taxTitle: 'Add a tax',
    vatPreset: 'VAT 14% (V009)',
    currencyTitle: 'Document currencies',
    addCurrency: 'Enable currency',
    remove: 'Stop using',
  },
};

export type Dictionary = typeof en;
